import React, { useEffect, useState } from "react";
import "./App.css";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import Aos from "aos";
import { Providers } from "./redux/providers";
import Dashboard from "./screens/Dashboard";
import Toast from "./components/Notifications/Toast";
import Payments from "./screens/Payments/Payments";
import Appointments from "./screens/Appointments";
import Patients from "./screens/Patients/Patients";
import Campaings from "./screens/Campaings";
import Services from "./screens/Services";
import Invoices from "./screens/Invoices/Invoices";
import Settings from "./screens/Settings";
import CreateInvoice from "./screens/Invoices/CreateInvoice";
import EditInvoice from "./screens/Invoices/EditInvoice";
import PreviewInvoice from "./screens/Invoices/PreviewInvoice";
import EditPayment from "./screens/Payments/EditPayment";
import PreviewPayment from "./screens/Payments/PreviewPayment";
import Medicine from "./screens/Medicine";
import PatientProfile from "./screens/Patients/PatientProfile";
import CreatePatient from "./screens/Patients/CreatePatient";
import Doctors from "./screens/Doctors/Doctors";
import DoctorProfile from "./screens/Doctors/DoctorProfile";
import Receptions from "./screens/Receptions";
import NewMedicalRecode from "./screens/Patients/NewMedicalRecode";
import NotFound from "./screens/NotFound";
import Login from "./screens/Login";
import Notifications from "./screens/Notifications";
import PatientsHistory from "./screens/Patients/PatientsHistory";
import PatientsAgenda from "./screens/Patients/PatientsAgenda";
import PatientsPayments from "./screens/Patients/PatientsPayments";
import PatientsInfo from "./screens/Patients/PatientsInfo";
import PatientsHealthInfo from "./screens/Patients/PatientsHealthInfo";
import Laboratories from "./screens/Patients/Laboratories.js";
import PatientLaboratories from "./screens/Patients/PatientLaboratories.js";
import { QueryClientProvider } from "@tanstack/react-query";
import queryClient from "./queryClient.js";
import ProtectedRoute from "./ProtectedRoute";
import { logger } from "./services/logger";
import LoggerDevTools from "./components/LoggerDevTools";
import { logRetryService } from "./services/logRetryService"; // ✅ Importar el servicio de reintentos
import ChatDock from "./components/ChatDock";
import AgentIcon from "./components/AgentIcon";
import { useSelector } from "react-redux";
import { selectIsAuthenticated } from "./redux/features/authSlice";

function App() {
  const [chatOpen, setChatOpen] = useState(false);
  // El asistente solo con sesión iniciada (mismo criterio que ProtectedRoute).
  const sesionIniciada = useSelector(selectIsAuthenticated);
  useEffect(() => {
    try {
      // Inicializar AOS con configuración
      Aos.init({
        duration: 1000,
        once: true,
        offset: 100,
      });

      // Forzar refresh de AOS después de la inicialización
      setTimeout(() => {
        Aos.refresh();
      }, 100);

      // ✅ Iniciar servicio de reintentos de logs (solo en producción)
      if (process.env.NODE_ENV === "production") {
        logRetryService.start();

        // Log de inicio del servicio de reintentos
        logger.info("Servicio de reintentos de logs iniciado", {
          component: "App",
          additionalData: {
            service: "LogRetryService",
            interval: "30 segundos",
            maxRetries: 5,
          },
        });
      } else {
        // En desarrollo, log informativo
        logger.debug("Modo desarrollo: logs almacenados localmente", {
          component: "App",
          additionalData: {
            service: "LogRetryService",
            status: "disabled_in_development",
          },
        });
      }

      // Log de inicio de la aplicación
      logger.info("Aplicación médica iniciada", {
        component: "App",
        additionalData: {
          environment: process.env.NODE_ENV,
          version: process.env.REACT_APP_VERSION || "1.0.0",
          path: window.location.pathname,
          userAgent: navigator.userAgent,
          logService:
            process.env.NODE_ENV === "production"
              ? "active"
              : "development_mode",
        },
      });
    } catch (error) {
      // Capturar errores de inicialización
      logger.error(error, {
        component: "App",
        additionalData: {
          context: "Initialization",
          errorType: "App_Initialization",
          phase: "startup",
        },
      });
    }

    // Cleanup function - ✅ Detener servicio al desmontar
    return () => {
      logRetryService.stop();

      logger.debug("Componente App desmontado", {
        component: "App",
        additionalData: {
          logRetryService: "stopped",
        },
      });
    };
  }, []);

  return (
    <Providers>
      <QueryClientProvider client={queryClient}>
        <Toast />
        <BrowserRouter>
          <Routes>
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <Notifications />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute>
                  <Payments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments/create"
              element={
                <ProtectedRoute>
                  <EditPayment />
                </ProtectedRoute>
              }
            />
            <Route
              path="/laboratories"
              element={
                <ProtectedRoute>
                  <Laboratories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/laboratories/:id"
              element={
                <ProtectedRoute>
                  <PatientLaboratories />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients"
              element={
                <ProtectedRoute>
                  <Patients />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/preview/:id"
              element={
                <ProtectedRoute>
                  <PatientProfile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/history/:id"
              element={
                <ProtectedRoute>
                  <PatientsHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/agenda/:id"
              element={
                <ProtectedRoute>
                  <PatientsAgenda />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/payments/:id"
              element={
                <ProtectedRoute>
                  <PatientsPayments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/patient_info/:id"
              element={
                <ProtectedRoute>
                  <PatientsInfo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/health_info/:id"
              element={
                <ProtectedRoute>
                  <PatientsHealthInfo />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/create"
              element={
                <ProtectedRoute>
                  <CreatePatient operation="create" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/patients/edit/:id"
              element={
                <ProtectedRoute>
                  <CreatePatient operation="edit" />
                </ProtectedRoute>
              }
            />
            <Route
              path="/appointments"
              element={
                <ProtectedRoute>
                  <Appointments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <Settings />
                </ProtectedRoute>
              }
            />
            {/* Rutas públicas */}
            <Route path="/login" element={<Login />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>

        {/* Componente de desarrollo para monitoreo de logs */}
        {process.env.NODE_ENV === "development" && <LoggerDevTools />}

        {/* Asistente IA: botón flotante + ventana de chat (POST a apiAI/askai).
            Solo con sesión iniciada: sin esto se veía en el login y cualquiera podía pedirle
            datos de pacientes sin usuario ni contraseña. Mismo criterio que ProtectedRoute. */}
        {sesionIniciada && !chatOpen && (
          <button
            type="button"
            onClick={() => setChatOpen(true)}
            className="fixed bottom-28 right-6 z-[45] w-16 h-16 rounded-full shadow-2xl hover:scale-105 transition-transform"
            title="Asistente IA"
            aria-label="Abrir asistente IA"
          >
            <AgentIcon className="w-16 h-16" />
          </button>
        )}
        <ChatDock isOpen={sesionIniciada && chatOpen} onClose={() => setChatOpen(false)} />
      </QueryClientProvider>
    </Providers>
  );
}

export default App;
